import React, { useEffect, useState, useRef } from 'react';
import { Funnel, MagnifyingGlass } from '@phosphor-icons/react';
import { MarketplaceFilters as FiltersType, countActiveFilters } from '../../lib/marketplaceFilters';
import { CategoryDropdown } from './filters/CategoryDropdown';
import { LocationDropdown } from './filters/LocationDropdown';
import { BudgetPopover } from './filters/BudgetPopover';
import { MoveInDropdown } from './filters/MoveInDropdown';
import { AdvancedFiltersPanel } from './filters/AdvancedFiltersPanel';
import { ActiveFilterChips } from './filters/ActiveFilterChips';
import { FilterPanel } from './filters/FilterPanel';
import { cn } from '../../lib/utils';
interface MarketplaceFiltersProps {
  filters: FiltersType;
  onChange: (filters: FiltersType) => void;
  properties: any[];
  selectedPropertyId: string | null;
  onSelectProperty: (id: string | null) => void;
  resultCount: number;
  onClearAll: () => void;
  onAddProperty?: () => void;
  isLoggedIn?: boolean;
}
export function MarketplaceFilters({
  filters,
  onChange,
  properties,
  selectedPropertyId,
  onSelectProperty,
  resultCount,
  onClearAll,
  onAddProperty,
  isLoggedIn = false
}: MarketplaceFiltersProps) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const filtreBtnWrapRef = useRef<HTMLDivElement>(null);
  const activeCount = countActiveFilters(filters);
  // Track viewport — sm = 640px.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 639px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const handleRemoveFilter = (key: keyof FiltersType, value?: string) => {
    if (Array.isArray(filters[key]) && value) {
      const arr = filters[key] as string[];
      onChange({
        ...filters,
        [key]: arr.filter(x => x !== value)
      });
    } else if (key === 'minBudgetEUR' || key === 'maxBudgetEUR') {
      onChange({
        ...filters,
        minBudgetEUR: null,
        maxBudgetEUR: null
      });
    } else if (key === 'moveIn') {
      onChange({
        ...filters,
        moveIn: 'any',
        moveInSpecificMonth: null
      });
    } else if (typeof filters[key] === 'boolean') {
      onChange({
        ...filters,
        [key]: false
      });
    } else if (key === 'minRating') {
      onChange({
        ...filters,
        minRating: 0
      });
    }
  };
  // On mobile, the advanced panel renders as a full-screen drawer instead of
  // an inline expansion. Use the same FilterPanel pattern as the 4 dropdowns
  // so the UX is consistent.
  const showInlinePanel = !isMobile && panelOpen;
  return <div className="mb-8 relative z-40" data-id="element-2463">
      {/* Sticky Bar Container */}
      <div className="sticky top-4 z-40 bg-dark-1/80 backdrop-blur-md rounded-2xl shadow-lg" data-id="element-2464">
        <div className={cn('flex flex-col sm:flex-row items-center gap-3 p-3 bg-dark-2 border border-dark-4 transition-all duration-300', showInlinePanel ? 'rounded-t-2xl border-b-transparent' : 'rounded-2xl')} data-id="element-2465">
          {/* 4 filter triggers — 2x2 grid on mobile, inline row on desktop.
           Each child has min-w-0 so long labels truncate cleanly instead
           of pushing the layout off-screen. */}
          <div className="grid grid-cols-2 gap-2 w-full sm:flex sm:items-center sm:gap-3 sm:flex-1 sm:flex-wrap md:flex-nowrap" data-id="element-2466">
            <CategoryDropdown selected={filters.categories} onChange={c => onChange({
            ...filters,
            categories: c
          })} resultCount={resultCount} data-id="element-2467" />
            <LocationDropdown selected={filters.locations} onChange={l => onChange({
            ...filters,
            locations: l
          })} resultCount={resultCount} data-id="element-2468" />
            <BudgetPopover minBudget={filters.minBudgetEUR} maxBudget={filters.maxBudgetEUR} onChange={(min, max) => onChange({
            ...filters,
            minBudgetEUR: min,
            maxBudgetEUR: max
          })} data-id="element-2469" />
            <MoveInDropdown selected={filters.moveIn} specificMonth={filters.moveInSpecificMonth} onChange={(sel, month) => onChange({
            ...filters,
            moveIn: sel as any,
            moveInSpecificMonth: month
          })} resultCount={resultCount} data-id="element-2470" />
          </div>

          {/* Right Actions */}
          <div ref={filtreBtnWrapRef} className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto shrink-0" data-id="element-2471">
            <button type="button" onClick={() => setPanelOpen(!panelOpen)} className={cn('flex-1 sm:flex-none min-w-0 inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl border text-sm font-medium transition-colors', panelOpen || activeCount > 0 ? 'bg-dark-3 border-lime-1 text-lime-1' : 'bg-dark-3 border-dark-4 text-light-2 hover:border-light-4')} data-id="element-2472">
              <Funnel size={16} className="shrink-0" data-id="element-2473" />
              <span className="truncate" data-id="element-2474">Filtre</span>
              {activeCount > 0 && <span className="inline-flex items-center justify-center min-w-[20px] h-5 rounded-full bg-lime-1 text-dark-1 text-[10px] font-bold px-1.5 shrink-0" data-id="element-2475">
                  {activeCount}
                </span>}
            </button>
            <button type="button" className="flex-1 sm:flex-none min-w-0 inline-flex items-center justify-center gap-2 h-10 px-4 sm:px-6 rounded-xl bg-lime-1 hover:bg-lime-2 text-dark-1 text-sm font-semibold transition-colors" data-id="element-2476">
              <MagnifyingGlass size={16} weight="bold" className="shrink-0" data-id="element-2477" />
              <span className="truncate" data-id="element-2478">Caută</span>
            </button>
          </div>
        </div>

        {/* Desktop inline expandable panel. On mobile we render the same
            content inside a FilterPanel drawer below. */}
        <div className={cn('overflow-hidden transition-all duration-300 ease-in-out', showInlinePanel ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0')} data-id="element-2479">
          {!isMobile && <AdvancedFiltersPanel filters={filters} onChange={onChange} properties={properties} selectedPropertyId={selectedPropertyId} onSelectProperty={onSelectProperty} resultCount={resultCount} onClose={() => setPanelOpen(false)} onReset={onClearAll} onAddProperty={onAddProperty} isLoggedIn={isLoggedIn} data-id="element-2480" />}
        </div>
      </div>

      {/* Mobile drawer for advanced filters */}
      {isMobile && <FilterPanel isOpen={panelOpen} onClose={() => setPanelOpen(false)} title="Toate filtrele" anchorRef={filtreBtnWrapRef} resultCount={resultCount} secondaryAction={activeCount > 0 ? {
      label: 'Resetează tot',
      onClick: onClearAll
    } : null} data-id="element-2481">
          <AdvancedFiltersPanel filters={filters} onChange={onChange} properties={properties} selectedPropertyId={selectedPropertyId} onSelectProperty={onSelectProperty} resultCount={resultCount} onClose={() => setPanelOpen(false)} onReset={onClearAll} onAddProperty={onAddProperty} isLoggedIn={isLoggedIn} compact data-id="element-2482" />
        </FilterPanel>}

      {/* Active Filter Chips (outside the sticky bar, below it). Wraps to
              multiple lines on narrow viewports so chips never push the page
              horizontally. */}
      {!showInlinePanel && <ActiveFilterChips filters={filters} onRemove={handleRemoveFilter} onClearAll={onClearAll} data-id="element-2483" />}
    </div>;
}